"use client";

import { Fragment, useEffect, useMemo, useState } from "react";
import Markdown from "@/components/atoms/Markdown";
import styles from "./styles.module.scss";
import cn from "clsx";
import { GAEvent, GA_EVENTS_PAYLOAD, IProgramPageSessionData } from "@/helpers/gaEvents";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGAPayload } from "@/helpers/getGAPayload";
import { usePlatformAndOS } from "@/helpers/usePlatformAndOS";
import { useDeviceInfo } from "@/helpers/userDeviceType";
import Image from "next/image";
import { StorageService } from "@/helpers/storage.service";
import Link from "next/link";
import { getGlobalStateValue } from "@/context/utils";
import IntersectionObserverView from "./IntersectionObserverView";
import {imgPathReplace} from '@/helpers/imgPathReplace';
import { isIndiaCountry } from "@/helpers/countryCode";
import strapiSingleAttribute from "@/api/strapiSingleAttribute";

export default function DegreeProgramCard({
  Heading,
  url_key,
  ProgramType,
  Title3,
  CourseFee,
  CourseFeeForUS,
  NonDiscountedCourceFeeForUS,
  NonDiscountedCourseFee,
  CourseStripText,
  YearsIcon,
  Image: ImageAttr,
  isSmall = false,
  scroll = false,
  isCertification = false,
  country = "IN",
  isNotClickable = false,
  title,
  isSponsoredCertification = false,
  sponsoredLogoURL = "",
  sponsoredLogoText = "",
  card_index,
  id,
  isExternal = false,
  isYearIcon = true,
  imageHeight= "auto",
  imageWidth= "auto"
}) {

  // `source` is a multi-media field: v4 returns { data: [{ attributes }] },
  // v5 returns a plain array. strapiSingleAttribute normalises both.
  const cardImage = strapiSingleAttribute(ImageAttr?.source);
  const yearsIcon = strapiSingleAttribute(YearsIcon?.source);

  const [pageTitle, setPageTitle] = useState<string>('');
  const [pageReferrer, setPageReferrer] = useState<string>('');
  const { platform = "", version = "" } = usePlatformAndOS();
  const deviceType = useDeviceInfo();
  
  const ParentComponent = useMemo(
    () =>
      isNotClickable
        ? { component: Fragment, params: null }
        : {
            component: Link,
            params: { href: url_key ? `${url_key}` : "#", scroll, target: isExternal ? "_blank" : "_self" ,  id:`online-degree-card-${card_index}`},
          }
    ,
    [isNotClickable, scroll, url_key],
  );

  useEffect(() => {
		setPageReferrer(document.referrer);
		setPageTitle(document.title);
		const handleTitleChange = () => {
		  setPageTitle(document.title);
		};
		document.addEventListener('titlechange', handleTitleChange); 
		return () => {
		  document.removeEventListener('titlechange', handleTitleChange);
		};
	}, []);

  const isMbaCard = Heading
    ? Heading.toLowerCase().includes("master of business administration")
    : false;
  const { PROGRAM_CARD_MBA } = GAEvent;
  const handleGaClick = () => {
    if(isMbaCard){
      const gaData = GA_EVENTS_PAYLOAD.get(PROGRAM_CARD_MBA);
      const gaOtherPayloadInfo = {
        platform,
        deviceType,
        version,
        pageTitle,
        pageReferrer,
        pageUrl : typeof window !== 'undefined' ? window.location.href : '',
      }
      const sessionData:IProgramPageSessionData = {
        isNavbarClick : false,
        isMbaPage : true
      }
      StorageService.set("programDetailsGAInfo", sessionData)
      const gaPayload = getGAPayload(gaData,new URLSearchParams(window.location.search),gaOtherPayloadInfo);
      pushToDataLayer(gaPayload);
    }else{
      const sessionData:IProgramPageSessionData = {
        isNavbarClick : false,
        isMbaPage : false
      }
      StorageService.set("programDetailsGAInfo", sessionData)
    }
    pushToDataLayer({
        'event':'card_interaction',
        'cta_text': Heading,
        'section_name': title || "NA",
        'page_type': getGlobalStateValue("pageType")
    });
  }

  return (
    <ParentComponent.component
      {...ParentComponent?.params}
      {...(!isNotClickable && { onClick: handleGaClick })}
      
    >
    
      <div
        className={cn(styles.card, {
          [styles.smallCard]: isSmall,
          [styles.biggerGradient]: isCertification,
        })}
      >
      <IntersectionObserverView>
        <img
         src={imgPathReplace(cardImage?.url)}
         alt={cardImage?.alternativeText || "Degree Card"}
         className={cn("w-full object-cover", styles.img, {
            [styles.bigImg]: isSmall,
          })}
          width={300}
          height={280}
          loading="lazy"
          title="Degree Card"
        />
      </IntersectionObserverView>
        
        {!!CourseStripText && !isCertification && (
          <p
            className={cn(styles.strip, {
              [styles.smallStrip]: isSmall,
            })}
          >
            {CourseStripText}
          </p>
        )}
        <div className={isYearIcon?styles.textContainer:"absolute lg:bottom-16 sm:bottom-[9rem] left-2 z-[1000]"}>
          {!CourseStripText && !isCertification && (
            <Markdown className={styles.programType}>
              {ProgramType?.split("_")?.join(" ")}
            </Markdown>
          )}
          <div className={styles.nameContainer}>
            <Markdown
              className={cn(styles.name, {
                [styles.certificationName]: isCertification,
              })}
            >
              {Heading}
            </Markdown>
            {isYearIcon && <div className={styles.yearContainer}>
              <img
                src={yearsIcon?.url}
                alt={
                  yearsIcon?.alternativeText ||
                  "Years Icon"
                }
                loading="lazy"
                width={yearsIcon?.width || 33}
                height={yearsIcon?.height || 33}
                title="Years Icon"
              />
              <span
                className={cn({
                  ["!tracking-normal"]: isCertification,
                })}
              >
                {YearsIcon?.title ? YearsIcon?.title : "N/A"}
              </span>
            </div>}
          </div>
          {!!Title3 && <p className={styles.desc}>{Title3}</p>}
          {!!CourseFee && (
            <p
              className={cn(styles.fees, {
                [styles.certificationFees]: isCertification,
              })}
            >
              <span>Course Fee: </span>
              {isCertification ? (
                <span className={styles.strikeThrough}>
                  {isIndiaCountry(country)
                    ? NonDiscountedCourseFee
                    : NonDiscountedCourceFeeForUS || 0}
                </span>
              ) : null}
              <span>
                {isIndiaCountry(country) ? CourseFee : CourseFeeForUS || 0}/-
              </span>
            </p>
          )}
         {isYearIcon && isSponsoredCertification && <div className={styles.inCollaborationWith}>
            <div>{sponsoredLogoText}</div>
            <img
              src={sponsoredLogoURL}
              width={imageWidth}
              height={imageHeight}
            />
            
        </div>}
      </div>
      </div>
    </ParentComponent.component>
  )
}
